/**
 * Recipekit-Anbindung für die selbst gebaute Rezept-Section.
 *
 * Die Rezept-Analytics (Views, Drucke, Produktklicks, Bewertungen, Umsatz-Attribution)
 * hängen nicht am Markup der Recipekit-Rezeptkarte, sondern am Recipekit-Web-Pixel unter
 * Einstellungen > Kundenereignisse. Der Pixel hört auf Shopify.analytics.publish().
 * Da diese Section eigenes Markup rendert, müssen die Events hier von Hand publiziert
 * werden — sonst bleibt das Recipekit-Dashboard leer.
 *
 * Bewertungen gehen zusätzlich an die Recipekit-API: Der Pixel-Event zählt die Abgabe
 * nur für die Statistik, gespeichert (und in die Metafelder recipe_rating / rating_count
 * zurückgeschrieben) wird sie erst durch den POST.
 */
(function () {
  'use strict';

  var RATING_ENDPOINT = 'https://my.recipekit.app/rating';
  var STORAGE_PREFIX = 'supplementa:recipe-rated:';

  // Das Web-Pixel-Bundle lädt asynchron und kann nach diesem Script bereitstehen.
  // Events bis dahin puffern, sonst geht der recipe_page_viewed-Event verloren.
  var PIXEL_POLL_MS = 200;
  var PIXEL_TIMEOUT_MS = 8000;

  var pending = [];
  var polling = false;

  // Der Theme-Editor rendert die Section bei jeder Änderung neu. Ohne diese Sperren
  // zählt jedes Neurendern als weiterer Rezeptaufruf im Recipekit-Dashboard.
  var viewed = Object.create(null);
  var printBound = false;
  var lastPrintFired = 0;

  function analyticsReady() {
    return !!(
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    );
  }

  function send(name, payload) {
    try {
      window.Shopify.analytics.publish(name, payload);
    } catch (e) {
      // publish wirft, wenn die Analytics-Bridge nicht erreichbar ist
    }
  }

  function pollForPixel() {
    if (polling) return;
    polling = true;

    var started = Date.now();
    var timer = setInterval(function () {
      if (analyticsReady()) {
        clearInterval(timer);
        polling = false;
        pending.splice(0).forEach(function (event) {
          send(event[0], event[1]);
        });
      } else if (Date.now() - started > PIXEL_TIMEOUT_MS) {
        clearInterval(timer);
        polling = false;
        pending.length = 0;
      }
    }, PIXEL_POLL_MS);
  }

  function publish(name, payload) {
    if (analyticsReady()) {
      send(name, payload);
      return;
    }
    pending.push([name, payload]);
    pollForPixel();
  }

  function readRating(key) {
    try {
      return Number(window.localStorage.getItem(key)) || 0;
    } catch (e) {
      return 0;
    }
  }

  function storeRating(key, value) {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (e) {
      // Privater Modus / voller Speicher: Bewertung wurde trotzdem gesendet
    }
  }

  /**
   * Hängt die Rezept-ID an einen Produktlink, damit ein späterer Warenkorb-Zugang
   * auf der Produktseite dem Rezept zugerechnet wird.
   * @param {HTMLAnchorElement} link
   * @param {string} recipeId
   */
  function tagProductLink(link, recipeId) {
    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return;
    }
    if (url.hostname !== window.location.hostname) return;
    if (url.pathname.indexOf('/products/') === -1) return;
    if (url.searchParams.get('recipe_id')) return;

    url.searchParams.set('recipe_id', recipeId);
    link.href = url.toString();
  }

  function bindIngredientLinks(root, recipeId) {
    var list = root.querySelector('.recipe__ingredients-list');
    if (!list) return;

    // Delegiert, weil Zutaten auch Links aus dem Recipekit-Rich-Text enthalten
    // können, die in Liquid nicht umgeschrieben werden.
    list.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (!link || !list.contains(link)) return;
      if (link.pathname.indexOf('/products/') === -1) return;

      tagProductLink(link, recipeId);
      publish('recipe_product_link_clicked', { recipe: recipeId });
    });
  }

  function firePrinted(recipeId) {
    // Der Druck-Button öffnet die Druckansicht, Cmd+P löst beforeprint aus.
    // Beide Wege können kurz hintereinander greifen; ein Druck bleibt ein Event.
    var now = Date.now();
    if (now - lastPrintFired < 2000) return;
    lastPrintFired = now;
    publish('recipe_printed', { recipe: recipeId });
  }

  function bindPrint(root, recipeId) {
    var trigger = root.querySelector('[data-recipe-print]');
    if (trigger) {
      trigger.addEventListener('click', function () {
        firePrinted(recipeId);
      });
    }

    if (!printBound) {
      printBound = true;
      window.addEventListener('beforeprint', function () {
        firePrinted(recipeId);
      });
    }
  }

  function bindRating(root, recipeId) {
    var widget = root.querySelector('[data-recipe-rate]');
    if (!widget) return;

    var stars = widget.querySelector('.recipe__rate-stars');
    var status = widget.querySelector('[data-recipe-rate-status]');
    var inputs = Array.prototype.slice.call(widget.querySelectorAll('.recipe__rate-input'));
    if (!stars || !inputs.length) return;

    var storageKey = STORAGE_PREFIX + recipeId;

    function lock(value, message) {
      inputs.forEach(function (input) {
        input.checked = Number(input.value) === value;
        input.disabled = true;
      });
      stars.classList.add('is-locked');
      if (status) status.textContent = message;
    }

    var previous = readRating(storageKey);
    if (previous) {
      lock(previous, 'Sie haben dieses Rezept mit ' + previous + ' von 5 Sternen bewertet.');
      return;
    }

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        var value = Number(input.value);
        if (!value) return;

        storeRating(storageKey, value);
        lock(value, 'Danke für Ihre Bewertung!');
        publish('recipe_rated', { recipe: recipeId, rating: value });

        fetch(RATING_ENDPOINT, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ recipe_id: recipeId, user_rating: value })
        }).catch(function () {
          // Der Dank bleibt stehen: Ein Fehlschlag hier ist für die Kundin
          // nicht handhabbar und der Pixel-Event ist bereits raus.
        });
      });
    });
  }

  function init(root) {
    var raw = root.getAttribute('data-recipe-id');
    if (!raw || root.hasAttribute('data-recipe-bound')) return;
    root.setAttribute('data-recipe-bound', '');

    // Recipekit erwartet die ID numerisch, so wie sie das eigene Widget sendet.
    var recipeId = /^\d+$/.test(raw) ? Number(raw) : raw;

    if (!viewed[raw]) {
      viewed[raw] = true;
      publish('recipe_page_viewed', { recipe: recipeId });
    }

    bindIngredientLinks(root, recipeId);
    bindPrint(root, recipeId);
    bindRating(root, recipeId);
  }

  function initAll() {
    document.querySelectorAll('.recipe[data-recipe-id]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Der Theme-Editor rendert die Section neu, ohne die Seite zu laden.
  document.addEventListener('shopify:section:load', initAll);
})();
